import React from "react";
import { HealthLog } from "../../../types/post";
import { formatProviderAttribution } from "../../../utils/providerAttribution";

interface RecoveryCardProps {
  latestHealthLog: (HealthLog & { id: string }) | null;
  hlrScore?: number | null;
  rhrPercentDiff?: number;
  hrvPercentDiff?: number;
  respPercentDiff?: number;
  loading28DayData?: boolean;
  className?: string;
  onClick?: () => void;
}

// Removed custom formatSleep function - now using formatSleepDuration utility

const Stat: React.FC<{
  label: string;
  value: string | number;
  percentDiff?: number;
  loading?: boolean;
}> = ({ label, value, percentDiff, loading }) => (
  <div className="gap-2 border-1 border border-base-300 bg-base-100 p-4 rounded-md text-left">
    <div className="text-[12px] tracking-wide text-base-content/80">
      {label}
      {loading && <span className="ml-1">...</span>}
    </div>
    <div className="flex items-center gap-2">
      <div className="text-3xl text-base-content font-bold">{value}</div>
      {percentDiff !== undefined && percentDiff !== 0 && (
        <span
          className={`text-xs font-medium px-2 py-1 rounded-md ${
            percentDiff > 0
              ? "bg-green-500/15 text-green-600"
              : percentDiff < 0
                ? "bg-base-200 text-base-content/80"
                : "bg-base-200 text-base-content/80"
          }`}
        >
          {percentDiff > 0 ? "+" : ""}
          {Math.round(percentDiff)}%
        </span>
      )}
    </div>
  </div>
);

const RecoveryCard: React.FC<RecoveryCardProps> = ({
  latestHealthLog,
  hlrScore,
  rhrPercentDiff = 0,
  hrvPercentDiff = 0,
  respPercentDiff = 0,
  loading28DayData = false,
  className = "",
  onClick,
}) => {
  const rhr = latestHealthLog?.rhr;
  const hrv = latestHealthLog?.hrv;
  const resp = latestHealthLog?.respRate;

  const isClickable = !!onClick;
  const Tag: any = isClickable ? "button" : "div";
  const attribution = formatProviderAttribution(latestHealthLog?.provider);

  // Format HLR score as +/- deviation from baseline
  const formatHLR = (hlr: number | null | undefined): string => {
    if (hlr == null) return "-";
    const diff = Math.round(hlr * 100 - 100);
    return `${diff > 0 ? "+" : ""}${diff}%`;
  };

  return (
    <Tag
      onClick={onClick}
      className={`rounded-xl p-4 w-full ${
        isClickable ? "transition-colors cursor-pointer" : ""
      } ${className}`}
    >
      <div className="flex justify-between items-start text-left mb-4">
        <div>
          <div className="text-sm font-heading font-semibold text-base-content">
            Health{" "}
            <span className="font-grotesk text-base-content/60 font-light">
              Stats
            </span>
          </div>
          {attribution && (
            <div className="text-xs text-base-content/50 mt-0.5">
              {attribution}
            </div>
          )}
        </div>
      </div>
      <div>
        {latestHealthLog || hlrScore != null ? (
          <div className=" space-y-4">
            <Stat
              label="Resting Heart Rate"
              value={rhr != null ? Math.round(rhr) : "-"}
            />
            <Stat
              label="Heart Rate Variability"
              value={hrv != null ? Math.round(hrv) : "-"}
            />
            <Stat
              label="Respiratory Rate"
              value={resp != null ? Math.round(resp * 10) / 10 : "-"}
            />
          </div>
        ) : (
          <div className="text-sm text-base-content/50 text-left">
            No health data for this day
          </div>
        )}
      </div>
    </Tag>
  );
};

export default RecoveryCard;
